/**
 * KeyFileForm — sekcja "Plik klucza prywatnego" z osobnym buttonem "Zapisz klucz".
 *
 * Część split-upload UI (refaktor 2026-06-01).
 *
 * Props:
 *   - adminConfig (object)  — bieżący stan z /admin/config
 *   - onSaved (callback)    — wywołane po pomyślnym zapisie
 */
import { useState, useRef } from "react";
import { logger } from "../../../utils/logger";
import { toast } from "sonner";
import { Save, Upload, FileCheck, CheckCircle, X, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadKeyFile } from "../services/ksefApi";
import { safeDetailMessage, safeErrorMessage } from "../utils/errorMessages";

export const KeyFileForm = ({ adminConfig, onSaved }) => {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const hasKey = !!adminConfig?.key_filename;

  const handleSave = async () => {
    if (!file) {
      toast.error("Wybierz plik klucza prywatnego");
      return;
    }
    setSaving(true);
    try {
      await uploadKeyFile(file);
      toast.success("Klucz zapisany");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onSaved) onSaved();
    } catch (err) {
      logger.error("Error saving key:", err);
      toast.error(
        safeDetailMessage(err.response) || safeErrorMessage(err) || "Błąd zapisywania klucza",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-testid="key-file-section">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
            <KeyRound className="w-4 h-4 text-gray-500" />
            Plik klucza prywatnego <span className="text-red-500">*</span>
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Wymagany. Akceptowane formaty: .pem, .key
          </p>
        </div>
        {hasKey && (
          <div className="flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
            <CheckCircle className="w-3 h-3" />
            Wgrany
          </div>
        )}
      </div>

      {adminConfig?.key_filename && !file && (
        <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-3 py-2">
          <FileCheck className="w-4 h-4 text-green-600" />
          <span className="font-mono break-all">{adminConfig.key_filename}</span>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept=".pem,.key"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid="key-file-input"
      />

      {file ? (
        <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
          <div className="flex items-center gap-2 text-sm text-blue-900 min-w-0">
            <Upload className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            className="text-blue-700 hover:text-blue-900"
            data-testid="key-file-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full"
          data-testid="key-file-pick-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          {hasKey ? "Wybierz nowy plik klucza" : "Wybierz plik klucza"}
        </Button>
      )}

      <Button
        onClick={handleSave}
        disabled={!file || saving}
        className="w-full"
        data-testid="key-file-save-btn"
      >
        {saving ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
        ) : (
          <Save className="w-4 h-4 mr-2" />
        )}
        Zapisz klucz
      </Button>
    </div>
  );
};

export default KeyFileForm;
